import Decimal from 'decimal.js';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { getCargoOperationForCargoAndOperationType, isManualCall as isManualRotationElement } from './stowageRotationUtils';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { OperationType } from 'src/constants/stowage/operationTypes';

export const getFormattedCargoNumber = (voyageNumber: string, cargoNumber: number, isManual: boolean): string => {
    if (!voyageNumber || !cargoNumber) {
        return '';
    }
    const prefix = isManual ? 'M' : '';
    return prefix + voyageNumber + '-' + cargoNumber.toString().padStart(3, '0');
};

export const getShortFormattedCargoNumber = (cargoNumber: number, isManual: boolean): string => {
    if (!cargoNumber) {
        return '';
    }
    const prefix = isManual ? 'M' : '';
    return prefix + cargoNumber.toString().padStart(3, '0');
};

export const isManualCargoOperation = (cargoOperation: RotationCargoOperation): boolean => {
    if (!cargoOperation?.cargo) return false;

    if (hasOrcaWebDataSource(cargoOperation.cargo)) return true;
    if (isTransferCargoOperation(cargoOperation)) return true;
    if (isLoadingOrUnloadingCargoOperation(cargoOperation) && hasOrcaWebDataSource(cargoOperation)) return true;

    return false;
};

export const hasOrcaWebDataSource = (item: { dataSource?: string }): boolean => {
    return item?.dataSource === 'OrcaWeb';
};

export const isManualCargo = (cargo: RotationCargo): boolean => {
    return isManualRotationElement(cargo);
};

/**
 * Gets the charterer name from the cargo, falling back to product's charterer name if not available.
 * @param cargo - The rotation cargo to get the charterer name from
 * @returns The charterer name or empty string if not found
 */
export const getChartererName = (cargo: RotationCargo | undefined | null): string => {
    if (!cargo) return '';
    return cargo.chartererName ?? cargo.product?.chartererName ?? '';
};

export const getRange = (minValue: number | undefined, maxValue: number | undefined): string | null => {
    return minValue !== null && minValue !== undefined && maxValue !== null && maxValue !== undefined
        ? minValue === maxValue
            ? `${minValue}`
            : `${minValue} / ${maxValue}`
        : null;
};

export const isLoadingOrUnloadingCargoOperation = (cargoOperation: RotationCargoOperation): boolean => {
    return [OperationType.LOADING, OperationType.UNLOADING].includes(cargoOperation.operationType as any);
};

export const isLoadingCargoOperation = (cargoOperation: RotationCargoOperation): boolean => cargoOperation.operationType === OperationType.LOADING;
export const isUnloadingCargoOperation = (cargoOperation: RotationCargoOperation): boolean =>
    cargoOperation.operationType === OperationType.UNLOADING;

export const isTransferCargoOperation = (cargoOperation: RotationCargoOperation): boolean => {
    return cargoOperation?.operationType === OperationType.TRANSFER;
};

export const isTransitOperation = (cargoOperation: RotationCargoOperation): boolean => {
    return cargoOperation.operationType === OperationType.TRANSIT;
};

type WeightType = 'nominated' | 'contract' | 'billOfLading';
export type OperationType = 'Loading' | 'Unloading';

export const getCargoOperationWeight = (
    stowagePlan: StowagePlan,
    cargo: RotationCargo,
    operationType: OperationType,
    weightType: WeightType,
    toBerthCall?: RotationBerthCall
): number => {
    const cargoOperations = getCargoOperationForCargoAndOperationType(stowagePlan, cargo, operationType, toBerthCall);
    const total = cargoOperations.reduce((sum, cargoOperation) => {
        let weight = new Decimal(0);
        switch (weightType) {
            case 'nominated':
                weight = new Decimal(cargoOperation.nominatedWeight ?? 0);
                break;
            case 'contract':
                weight = new Decimal(cargoOperation.contractWeight ?? 0);
                break;
            case 'billOfLading':
                weight = new Decimal(cargoOperation.billOfLadingWeight ?? 0);
                break;
        }
        return sum.plus(weight);
    }, new Decimal(0));
    return total.toNumber();
};

/**
 * Checks if a cargo operation has an edited contract weight (user override active)
 * For loading operations: checks the operation-level flag
 * For unloading operations: checks the cargo-level flag (inherited from loading operation)
 * @param cargoOperation - The cargo operation to check
 * @returns True if the cargo operation has an active contract weight override
 */
export const hasEditedContractWeight = (cargoOperation: RotationCargoOperation): boolean => {
    // Check operation-level flag first (for loading operations)
    if (cargoOperation.isContractWeightEdited === true) {
        return true;
    }
    // For unloading operations, check the cargo-level flag (inherited from loading)
    if (cargoOperation.operationType === OperationType.UNLOADING && cargoOperation.cargo?.isContractWeightEdited === true) {
        return true;
    }
    return false;
};

/**
 * Checks if a cargo has any operations with edited contract weight
 * @param cargo - The cargo to check
 * @returns True if the cargo has an active contract weight override on any operation
 */
export const cargoHasEditedContractWeight = (cargo: RotationCargo): boolean => {
    return cargo.isContractWeightEdited === true;
};

/**
 * Gets all cargo operations with edited contract weights from a stowage plan
 * @param stowagePlan - The stowage plan to search
 * @returns Array of cargo operations with active contract weight overrides
 */
export const getEditedContractWeightCargoes = (stowagePlan: StowagePlan): RotationCargoOperation[] => {
    return stowagePlan.cargoOperations?.filter(hasEditedContractWeight) ?? [];
};

/**
 * Gets the original IMOS contract weight for a cargo operation (if available)
 * @param cargoOperation - The cargo operation to get the IMOS weight for
 * @returns The original IMOS contract weight, or undefined if not set
 */
export const getOriginalIMOSWeight = (cargoOperation: RotationCargoOperation): number | undefined => {
    return cargoOperation.originalContractWeightFromIMOS;
};

/**
 * Determines if weight changes should propagate to unloading operations for a cargo operation
 * @param cargoOperation - The loading cargo operation being edited
 * @param unloadingOperationsCount - Number of unloading operations for this cargo
 * @returns True if weight changes should propagate to unloading operations
 */
export const shouldPropagateWeightToUnloading = (cargoOperation: RotationCargoOperation, unloadingOperationsCount: number): boolean => {
    // Manual cargo operations: always propagate to all unloading operations
    if (isManualCargoOperation(cargoOperation)) {
        return true;
    }

    // IMOS cargo operations with edited contract weight: propagate to all unloading operations
    // Single unloading: direct copy of weight
    // Multiple unloadings: proportional distribution based on original allocation
    if (cargoOperation.isContractWeightEdited && unloadingOperationsCount > 0) {
        return true;
    }

    return false;
};

/**
 * Checks if any cargo operations in a collection have edited contract weight
 * @param cargoOperations - Array of cargo operations to check
 * @returns True if any cargo operation has an active contract weight override
 */
export const hasAnyEditedContractWeight = (cargoOperations: RotationCargoOperation[]): boolean => {
    return cargoOperations?.some(hasEditedContractWeight) ?? false;
};

/**
 * Result of proportional weight distribution calculation for a single unloading operation
 */
export interface ProportionalWeightResult {
    operationGuid: string;
    newContractWeight: number;
    newNominatedWeight: number;
}

/**
 * Calculates proportional weight distribution for multiple unloading operations based on new total loading weight.
 * Distributes the new total loading weight among unloading operations based on their original proportions.
 *
 * @param newTotalLoadingContractWeight - The new total contract weight from all loading operations
 * @param newTotalLoadingNominatedWeight - The new total nominated weight from all loading operations
 * @param unloadingOperations - Array of unloading cargo operations
 * @returns Array of weight distribution results for each unloading operation
 *
 * @example
 * // Loading: 1000 MT → 1100 MT (+10%)
 * // Unloading 1: 400 MT (40%) → 440 MT
 * // Unloading 2: 600 MT (60%) → 660 MT
 */
export const calculateProportionalWeightDistribution = (
    newTotalLoadingContractWeight: number,
    newTotalLoadingNominatedWeight: number,
    unloadingOperations: RotationCargoOperation[]
): ProportionalWeightResult[] => {
    if (!unloadingOperations || unloadingOperations.length === 0) {
        return [];
    }

    // Calculate current total unloading weights
    const totalUnloadingContractWeight = unloadingOperations.reduce((sum, op) => sum + (op.contractWeight ?? 0), 0);
    const totalUnloadingNominatedWeight = unloadingOperations.reduce((sum, op) => sum + (op.nominatedWeight ?? 0), 0);

    // Handle edge case: all unloadings have zero weight
    if (totalUnloadingContractWeight === 0 && totalUnloadingNominatedWeight === 0) {
        return unloadingOperations.map((op) => ({
            operationGuid: op.cargoOperationGuid,
            newContractWeight: 0,
            newNominatedWeight: 0,
        }));
    }

    const results: ProportionalWeightResult[] = [];
    let remainingContractWeight = newTotalLoadingContractWeight;
    let remainingNominatedWeight = newTotalLoadingNominatedWeight;

    // Process all but the last operation
    for (let i = 0; i < unloadingOperations.length - 1; i++) {
        const op = unloadingOperations[i];
        const currentContractWeight = op.contractWeight ?? 0;
        const currentNominatedWeight = op.nominatedWeight ?? 0;

        // Calculate proportions based on original weights
        const contractProportion =
            totalUnloadingContractWeight > 0 ? currentContractWeight / totalUnloadingContractWeight : 1 / unloadingOperations.length;
        const nominatedProportion =
            totalUnloadingNominatedWeight > 0 ? currentNominatedWeight / totalUnloadingNominatedWeight : 1 / unloadingOperations.length;

        // Calculate new weights using proportions, rounded to 3 decimal places
        const newContractWeight = Math.round(newTotalLoadingContractWeight * contractProportion * 1000) / 1000;
        const newNominatedWeight = Math.round(newTotalLoadingNominatedWeight * nominatedProportion * 1000) / 1000;

        results.push({
            operationGuid: op.cargoOperationGuid,
            newContractWeight,
            newNominatedWeight,
        });

        remainingContractWeight -= newContractWeight;
        remainingNominatedWeight -= newNominatedWeight;
    }

    // Last operation gets the remainder to avoid rounding errors
    const lastOp = unloadingOperations[unloadingOperations.length - 1];
    results.push({
        operationGuid: lastOp.cargoOperationGuid,
        newContractWeight: Math.round(remainingContractWeight * 1000) / 1000,
        newNominatedWeight: Math.round(remainingNominatedWeight * 1000) / 1000,
    });

    return results;
};
